import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { buildRelatedHubs } from "@/lib/seo/related-hubs";
import { getCity } from "@/lib/seo/cities";
import { norrkopingTrust, norrkopingProductionPricing, norrkopingProcess } from "@/content/landing/norrkoping";

const FAQ: LandingFAQ[] = [
  {
    "q": "Vad är skillnaden mellan filmproduktion och en filmbyrå?",
    "a": "Filmproduktion är hantverket. Filmbyrå inkluderar även strategi och kreativ idé. Fenny levererar båda i samma fasta pris."
  },
  {
    "q": "Hur ser en typisk filmproduktion i Norrköping ut?",
    "a": "Brief (v 1) → manus & storyboard (v 2) → pre-prod (v 3) → inspelning 1–3 dagar (v 3–4) → klipp, grade, ljudmix (v 4–6) → leverans."
  },
  {
    "q": "Vad kostar filmproduktion i Norrköping 2026?",
    "a": "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Brand-/industrifilm: 250 000–700 000 kr. Pulse: från 14 995 kr/mån."
  },
  {
    "q": "Hur snabbt kan ni vara igång?",
    "a": "Första möte inom 5 arbetsdagar. Pulse: första klipp inom 10–14 dagar."
  },
  {
    "q": "Var i Östergötland filmar ni?",
    "a": "Hela regionen — Industrilandskapet, hamnen, datacenter-klustret och Norrköpings hamn samt Linköping, Söderköping, Finspång och Östergötland. Drönar-tillstånd för aktuella zoner."
  },
  {
    "q": "Vilken utrustning använder ni?",
    "a": "Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus. DJI-drönare. Post i DaVinci Resolve och Premiere."
  },
  {
    "q": "Klarar ni krävande industri- och säkerhetsmiljöer?",
    "a": "Ja. Vana vid logistik, datacenter och tillverkning. PPE, NDA, säkerhetsbriefer och samordning med skiftledare."
  },
  {
    "q": "Tar ni hand om manus och regi?",
    "a": "Ja. Vana att översätta komplex verksamhet till säljande film."
  },
  {
    "q": "Hur många versioner ingår?",
    "a": "En master per format. Engelska versioner för export-/forskningskommunikation vid behov."
  },
  {
    "q": "Får vi raw-materialet?",
    "a": "Standard: mastrade filer + stillbilder. Raw lagras 12 månader."
  },
  {
    "q": "Kan vi vara med på set?",
    "a": "Ja. Live-feed via Frame.io för distans-kunder."
  },
  {
    "q": "Hur fungerar betalning?",
    "a": "50% vid produktionsstart, 50% vid leverans."
  }
];

const CITY = getCity("norrkoping")!;
const RELATED = buildRelatedHubs(CITY, "filmbyra");

export const Route = createFileRoute("/filmbyra_/norrkoping")({
  head: () => ({
    meta: [
      { title: "Filmbyrå Norrköping — produktion i Östergötland | Fenny" },
      { name: "description", content: "Filmbyrå för Norrköping och Linköping, Söderköping, Finspång och Östergötland. Strategi, produktion och versionering — fast pris, 4–6 v." },
      { property: "og:title", content: "Filmbyrå Norrköping — Fenny" },
      { property: "og:description", content: "Filmbyrå för Norrköping och Östergötland." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmbyra/norrkoping" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmbyra/norrkoping" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmbyra/norrkoping", name: "Filmbyrå Norrköping — produktion i Östergötland | Fenny", description: "Filmbyrå för Norrköping och Linköping, Söderköping, Finspång och Östergötland. Strategi, produktion och versionering — fast pris, 4–6 v."})) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Norrköping", path: "/filmbyra/norrkoping", description: "Filmbyrå för Norrköping och Östergötland. Strategi, produktion och versionering.", postalRegion: "Östergötlands län" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Filmbyrå", path: "/filmbyra" }, { name: "Norrköping", path: "/filmbyra/norrkoping" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Filmbyrå · Norrköping"
      title={<>Filmbyrån som faktiskt förstår Norrköping och Östergötland.</>}
      lede="Fenny är en filmbyrå med produktionsteam som producerar i Norrköping och Linköping, Söderköping, Finspång och Östergötland. Vi producerar reklamfilm, brand storytelling, företagsfilm och employer branding för varumärken inom logistik, datacenter, tillverkning, e-handel och offentlig sektor."
      tldr={<p>Fenny är filmbyrån för <strong>Norrköping</strong>. Vi producerar i Linköping, Söderköping, Finspång och Östergötland. <strong>Fast pris</strong>, 4–6 v.</p>}
      ctaPrimary={{ label: "Få offert inom 48 h", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="filmbyra-norrkoping"
      trust={norrkopingTrust}
      bullets={[
        { title: "Lokalt fokus", body: "Vi producerar i Linköping, Söderköping, Finspång och Östergötland. Resa inom regionen inkluderat." },
        { title: "Östergötland-vana", body: "Djup erfarenhet av logistik, datacenter och tillverkning." },
        { title: "Hela kedjan inhouse", body: "Strategi, regi, foto, ljud, klipp, grade." },
      ]}
      pricing={{ heading: "Vad det kostar", intro: "Riktpriser för 2026.", rows: norrkopingProductionPricing, footnote: "Alla priser exkl. moms." }}
      process={{ heading: "Från brief till leverans", intro: "Linjär process med skriftligt godkännande.", steps: norrkopingProcess }}
      sections={[
        localProductionSection("norrkoping", "Norrköping"),
        { id: "fenny-i-staden", heading: "Fenny i Norrköping", body: <p>Vi jobbar regelbundet i Norrköping. Skarp förplanering, koncentrerad inspelning och möjlighet att kombinera Norrköping med Linköping, Söderköping, Finspång och Östergötland i samma produktion.</p> },
        { id: "vem", heading: "Vem vi passar för i Östergötland", body: <p>Vi producerar för varumärken inom logistik, datacenter, tillverkning, e-handel och offentlig sektor — och levererar <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">kundcase-film</a>.</p> },
        { id: "modeller", heading: "Hur vi levererar", body: <p>Pulse-abonnemang för löpande output, Prime för enskilda större produktioner. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna pris</a>.</p> },
      ]}
      faq={FAQ}
      caseLimit={6}
      related={{
        eyebrow: "Relaterat",
        title: <>Mer för {"Norrköping"}-team</>,
        formats: RELATED.formats,
        siblings: RELATED.siblings,
        comparisons: RELATED.comparisons,
      }}
      closing={{ title: "Söker ni filmbyrå i Norrköping?", body: "30 minuter där vi mappar er insats och nästa steg i Östergötland.", primary: { label: "Boka strategimöte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
